/* assets/product-addons.js
   Instant cart add-ons (no ATC blocking)
   - Reads add-on selections from the DOM
   - Lets Instant add the main product
   - Adds selected add-ons via /cart/add.js
   - Supports dependencies:
       If a selected option has:
         data-depends-on-group="anchors"
         data-depends-qty-add="1"
       then it adds +1 of whatever variant is currently selected in addon_anchors.
   - Uses Instant.api.updateCart() to refresh the cart UI safely
   - Updates *all* Instant CART_COUNT badges (nav/header/overlay) via /cart.js
   - Applies default radio selection on initial render + Instant re-renders
*/
(() => {
  const ROOT_SELECTOR = '[data-product-addons]';

  let isAddingAddOns = false;

  const ceilDiv = (a, b) => Math.ceil(a / b);

  function getRoot() {
    return document.querySelector(ROOT_SELECTOR);
  }

  function getMainQtyFromPage() {
    const qtyEl =
      document.querySelector('form[action^="/cart/add"] input[name="quantity"]') ||
      document.querySelector('input[name="quantity"]');

    return qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;
  }

  function computeQty(rule, mainQty, factor, perX) {
    const f = Math.max(1, parseInt(factor, 10) || 1);
    const x = Math.max(1, parseInt(perX, 10) || 1);

    switch (rule) {
      case 'per_order':
        return 1 * f;
      case 'multiplier':
        return mainQty * f;
      case 'per_x_main':
        return ceilDiv(mainQty, x) * f;
      case 'match_main':
      default:
        return mainQty * f;
    }
  }

  function applySingleGroupDefaults(root) {
    const groups = root.querySelectorAll(
      'fieldset[data-addon-group][data-selection-type="single"]'
    );

    groups.forEach((fs) => {
      const groupKey = fs.getAttribute('data-addon-group');
      if (!groupKey) return;

      const name = `addon_${groupKey}`;
      const radios = fs.querySelectorAll(
        `input[type="radio"][name="${CSS.escape(name)}"]`
      );
      if (!radios.length) return;

      const checked = fs.querySelector(
        `input[type="radio"][name="${CSS.escape(name)}"]:checked`
      );
      if (checked) return;

      const firstReal = Array.from(radios).find(
        (r) => r.value && r.value.trim() !== ''
      );
      if (firstReal) firstReal.checked = true;
    });
  }

  function setInitialAddonDefaults() {
    const root = getRoot();
    if (!root) return;
    applySingleGroupDefaults(root);
  }

  function collectAddOnItems(root, mainQty) {
    const items = [];
    const checkedInputs = root.querySelectorAll('input[name^="addon_"]:checked');

    checkedInputs.forEach((input) => {
      if (!input.value) return; // "No add-on required"

      const wrapper = input.closest('[data-addon-option]');
      if (!wrapper) return;

      const rule = wrapper.getAttribute('data-qty-rule') || 'match_main';
      const factor = wrapper.getAttribute('data-qty-factor') || '1';
      const perX = wrapper.getAttribute('data-per-x') || '1';

      const qty = computeQty(rule, mainQty, factor, perX);
      if (qty > 0) items.push({ id: String(input.value), quantity: qty });
    });

    return items;
  }

  function getSelectedVariantIdForGroup(root, groupKey) {
    if (!groupKey) return null;
    const name = `addon_${groupKey}`;
    const checked = root.querySelector(
      `input[name="${CSS.escape(name)}"]:checked`
    );
    if (!checked) return null;
    if (!checked.value) return null; // "none"
    return String(checked.value);
  }

  function upsertItem(map, id, qty) {
    const q = parseInt(qty, 10) || 0;
    if (!id || q <= 0) return;

    const key = String(id);
    const prev = map.get(key);
    map.set(key, { id: key, quantity: (prev ? prev.quantity : 0) + q });
  }

  async function addItemsSequential(items) {
    for (const item of items) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item),
      });
      if (!res.ok) throw new Error('Failed to add add-on item');
    }
  }

  async function refreshInstantCartCountEverywhere() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;

      const cart = await res.json();
      const count = typeof cart.item_count === 'number' ? cart.item_count : null;
      if (count === null) return;

      document
        .querySelectorAll('[data-instant-dynamic-content-source="CART_COUNT"]')
        .forEach((el) => {
          el.textContent = String(count);
        });

      if (typeof cart.total_price === 'number') {
        const price = (cart.total_price / 100).toLocaleString('en-GB', {
          style: 'currency',
          currency: 'GBP',
        });

        document
          .querySelectorAll('[data-instant-dynamic-content-source="CART_SUBTOTAL_PRICE"]')
          .forEach((el) => {
            el.textContent = price;
          });
      }
    } catch (e) {
      // non-fatal
    }
  }

  async function refreshInstantCartUiSafely() {
    try {
      if (window.Instant?.api?.updateCart) {
        await window.Instant.api.updateCart();
      }
    } catch (e) {
      console.warn('Instant updateCart failed:', e);
    }

    refreshInstantCartCountEverywhere();
  }

  // Defaults now + on Instant re-render
  setInitialAddonDefaults();
  document.addEventListener('DOMContentLoaded', setInitialAddonDefaults);

  let defaultsTimer = null;
  const mo = new MutationObserver(() => {
    clearTimeout(defaultsTimer);
    defaultsTimer = setTimeout(setInitialAddonDefaults, 50);
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const res = await originalFetch(...args);

    try {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
      const isCartAdd = url && url.includes('/cart/add');

      if (!isCartAdd) return res;
      if (isAddingAddOns) return res;
      if (!res.ok) return res;

      const root = getRoot();
      if (!root) return res;

      applySingleGroupDefaults(root);

      const mainQty = getMainQtyFromPage();

      // Base add-ons
      const baseItems = collectAddOnItems(root, mainQty);

      // Merge + apply dependencies
      const itemMap = new Map();
      baseItems.forEach((it) => upsertItem(itemMap, it.id, it.quantity));

      // If a selected option declares a dependency, add depends_qty_add of the selected variant
      // from depends_on_group_key (e.g. extra post adds +1 of whichever anchor was chosen).
      const selectedInputs = root.querySelectorAll('input[name^="addon_"]:checked');

      selectedInputs.forEach((input) => {
        if (!input.value) return; // ignore "none"

        const wrap = input.closest('[data-addon-option]');
        if (!wrap) return;

        const dependsGroup = (wrap.getAttribute('data-depends-on-group') || '')
  .trim()
  .toLowerCase();

const dependsVariant = (wrap.getAttribute('data-depends-on-variant') || '')
  .trim();

const dependsQtyAdd = parseInt(
  (wrap.getAttribute('data-depends-qty-add') || '0').trim(),
  10
) || 0;

if (dependsQtyAdd <= 0) return;

let dependentVariantId = null;

if (dependsVariant) {
  dependentVariantId = dependsVariant;
} else if (dependsGroup) {
  dependentVariantId = getSelectedVariantIdForGroup(root, dependsGroup);
}

if (!dependentVariantId) return;

const triggerRule = wrap.getAttribute('data-qty-rule') || 'match_main';
const triggerFactor = wrap.getAttribute('data-qty-factor') || '1';
const triggerPerX = wrap.getAttribute('data-per-x') || '1';

const triggerQty = computeQty(triggerRule, mainQty, triggerFactor, triggerPerX);

upsertItem(itemMap, dependentVariantId, triggerQty * dependsQtyAdd);
 });

      const finalItems = Array.from(itemMap.values());
      if (!finalItems.length) return res;

      isAddingAddOns = true;
      await addItemsSequential(finalItems);
      isAddingAddOns = false;

      refreshInstantCartCountEverywhere();

      setTimeout(() => {
        refreshInstantCartUiSafely();
      }, 180);

      return res;
    } catch (err) {
      isAddingAddOns = false;
      console.warn('Add-ons error:', err);
      return res;
    }
  };
})();